import Icon from 'atoms/Icons/Icon';
import color from 'atoms/colors/colors';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Heading from 'quarks/Heading';
import Image from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';

import Badge from 'molecules/Badge/Badge';
import Breadcrumbs from 'molecules/Breadcrumbs';

import richTextParser from 'utils/richTextParser';

import type { ContentfulEntityPerson } from 'graphqlTypes';
import type { FC } from 'react';

const AuthorHero: FC<ContentfulEntityPerson> = ({
  fullName,
  headshot,
  role,
  company,
  biography,
  topContributor,
  evidenceBased,
  linkedInUrl,
  twitterUrl,
  facebookUrl,
  instagramUrl,
  githubUrl,
  dribbbleUrl,
  threadsUrl,
}) => {
  const breadcrumbs = [{ label: 'Authors', link: '/authors' }, { label: fullName || '' }];
  const authorTags = [
    { tag: topContributor, label: 'Top Contributor', icon: 'bookmark' },
    { tag: evidenceBased, label: 'Evidence Based', icon: 'check-verified-02' },
  ] as const;
  const socialLinks = [
    { url: linkedInUrl, icon: 'linkedin-full' },
    { url: twitterUrl, icon: 'twitter' },
    { url: facebookUrl, icon: 'facebook-full' },
    { url: instagramUrl, icon: 'instagram' },
    { url: githubUrl, icon: 'github' },
    { url: dribbbleUrl, icon: 'dribbble' },
    { url: threadsUrl, icon: 'threads' },
  ] as const;

  return (
    <>
      <Container
        position="absolute"
        backgroundColor="gray-100"
        left={0}
        right={0}
        top={0}
        height="544px"
        sm={{ height: '694px' }}
        lg={{ height: '460px' }}
        zIndex={0}
      />
      <Flex flexDirection="column" position="relative" zIndex={10}>
        <Breadcrumbs breadcrumbs={breadcrumbs} marginBottom={32} md={{ marginBottom: 48 }} xl={{ marginBottom: 56 }} />
        <Flex
          flexDirection="column"
          backgroundColor="common-white"
          borderRadius="16px"
          paddingAll={24}
          boxShadow="xl"
          columnGap="72px"
          rowGap="24px"
          md={{ paddingAll: 32, rowGap: '32px' }}
          lg={{ flexDirection: 'row' }}
          justifyContent="space-between"
        >
          <Flex flexDirection="column" width="fit-content">
            <Badge backgroundColor="primary-100" textColor="primary-700" size="sm" marginBottom={8}>
              Author
            </Badge>
            <Heading as="h1" textStyle="sm" md={{ textStyle: 'md' }} lg={{ textStyle: 'lg' }} marginBottom={8}>
              {fullName}
            </Heading>
            <Text textStyle="md" md={{ textStyle: 'lg' }} lg={{ textStyle: 'xl' }} marginBottom={24}>
              {role} {role && company?.name && 'at'} {company?.name}
            </Text>
            {(topContributor || evidenceBased) && (
              <Flex
                columnGap="32px"
                rowGap="16px"
                marginBottom={32}
                flexDirection="column"
                md={{ flexDirection: 'row' }}
              >
                {authorTags.map(
                  ({ tag, label, icon }) =>
                    tag && (
                      <Flex key={label} alignItems="center" gap="14px">
                        <Icon id={icon} size={20} stroke={color.primary[500]} />
                        <Text textStyle="md">{label}</Text>
                      </Flex>
                    ),
                )}
              </Flex>
            )}
            {biography && (
              <Text
                as={Flex}
                textStyle="md"
                md={{ textStyle: 'lg' }}
                textColor="gray-700"
                css={`
                  flex-direction: column;
                  gap: 8px;
                `}
              >
                {richTextParser(biography)}
              </Text>
            )}
            {socialLinks.some(({ url }) => !!url) && (
              <Flex flexDirection="row" marginTop={24} gap="16px">
                {socialLinks.map(
                  ({ url, icon }) =>
                    url && (
                      <Link href={url} key={url}>
                        <Icon id={icon} size={24} fill={color.primary[500]} />
                      </Link>
                    ),
                )}
              </Flex>
            )}
          </Flex>
          {headshot ? (
            <Image
              image={headshot}
              alt={`${fullName}'s headshot`}
              borderRadius="8px"
              width="100%"
              loading="eager"
              lg={{ maxWidth: '500px' }}
            />
          ) : (
            <Flex
              backgroundColor="primary-50"
              width="100%"
              lg={{ maxWidth: '500px' }}
              borderRadius="8px"
              justifyContent="center"
            >
              <Icon id="default-headshot" size={380} />
            </Flex>
          )}
        </Flex>
      </Flex>
    </>
  );
};

export default AuthorHero;
